import {useState} from 'react'
import {useParams} from 'react-router-dom'

import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import {useForm} from 'react-hook-form'

import FormWrapper from '../../shared/FormWrapper'
import Input from '../../shared/Input'

import {connect} from '../../../state/hooks/tools'
import {createResource} from '../../../lib/asyncMethods'
import {isWrongPassword} from '../../../lib/recovery'
import {RELEASED_KEY_SHARES} from '../../../state/actionTypes'

const RELEASE = 'released-key-shares'

/**
 * What a recovery contact sees when the person who named them has been locked out.
 *
 * Unlike the invitation this page has no one to address. The link carries a token that resolves to
 * a recovery contact, and every endpoint that would turn that into a name — theirs or the account
 * holder's — needs a session this visitor will never have. So the page opens on the arrangement
 * rather than on a person, and leans on the email, which did have both names, to have set it up.
 *
 * The token is spent by the request itself, so there is nothing to check before showing the form;
 * a link already used comes back as a refusal here rather than a page that never renders.
 */
const ReleaseKeyShare = props => {
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const params = useParams()
  const methods = useForm()
  const {errors} = methods.formState

  const release = ({passphrase}) => {
    setError('')

    props.releaseShare({endpoint: RELEASE, attributes: {passphrase, token: params.token}}).then(response => {
      if (response.success)
        return setMessage(response.message)

      // the passphrase is the only thing this person could have got wrong; anything else means the
      // link itself is spent, which is worth saying plainly rather than as a generic refusal
      return setError(
        isWrongPassword(response) ?
          "That isn't the password you chose when you agreed to help. Check it and try again." :
          'This link has already been used, or a newer one has replaced it. Ask them to send you another.'
      )
    })
  }

  if (message.length > 0) {
    return (
      <Paper elevation={3} component='div' className='release-key-share center-layout'>
        <Typography variant='h5' gutterBottom>Your piece has been sent</Typography>
        <Typography variant='body1' gutterBottom>{message}</Typography>
        <Typography variant='body2'>There's nothing else to do, and you can close this page.</Typography>
      </Paper>
    )
  }

  return (
    <Paper elevation={3} component='div' className='release-key-share center-layout'>
      <Typography variant='h5' gutterBottom>Send back your piece of the key</Typography>
      <Typography variant='body1' gutterBottom>
        A while ago you agreed to help someone get back into their account if they ever lost their
        password. That's happened, and they've asked for your piece.
      </Typography>
      <Typography variant='body2' gutterBottom>
        Enter the password you chose when you agreed to help. It's the only thing that can unlock
        your piece, and we can't reset it for you.
      </Typography>
      {error.length > 0 && <Typography variant='body1' color='error' gutterBottom>{error}</Typography>}
      <FormWrapper methods={methods} handleSubmit={release}>
        <Input
          error={!!errors.passphrase}
          label='Password'
          name='passphrase'
          type='password'
          options={{required: 'Password is required'}}
          helperText={errors.passphrase?.message}
          margin='dense'
        />
        <Button disabled={props.loading} type='submit' variant='contained' sx={{color: '#F7F7F7'}}>
          Send my piece
        </Button>
      </FormWrapper>
      <Typography className='caution' variant='body2' sx={{marginTop: '15px'}}>
        If you weren't expecting this, check with them directly before going ahead.
      </Typography>
    </Paper>
  )
}

const mapStateToProps = ({releasedKeyShares}) => ({loading: releasedKeyShares.loading})

const actionCreators = ({releasedKeyShares}) => ({
  releaseShare: createResource(releasedKeyShares, RELEASED_KEY_SHARES)
})

export default connect(mapStateToProps, actionCreators)(ReleaseKeyShare)
